// Narration service config (Post a Project, Atrium Stage 7) — standalone path.
//
// Source of truth = the Rails backend, NOT the design prototype:
//   whitelist  app/controllers/api/v1/project_postings_controller.rb (narration_posting_params)
//   validation app/models/job_postings/job_postings/step_validations.rb (narration__one, else-branch)
//   cost       app/models/job_postings/narration_posting.rb (#cost)
//   parity     tasks/post-a-project/parity/design-media.md §3
//
// Narration is NOT eligible_for_qa_qc, so NO AI consent and NO QA/QC step exist for
// it (the shared add-on step omits QA/QC; this config carries neither). The optional
// doc field key is `requirements` (NOT `file`); narration has no `file` attr.
// niche switches source: WRITING_GENRES when fiction, else WRITING_NICHES.

import {
  WRITING_NICHES,
  WRITING_GENRES,
  NARRATION_ACCENTS,
  NARRATION_GENDERS,
  RATES,
} from "../options"

// string[] -> [{ value, label }] for select fields.
const asOptions = (arr) => arr.map((v) => ({ value: v, label: v }))

const money = (n) => Math.round(n * 100) / 100

export const narration = {
  key: "narration",
  postingKey: "narration_posting",
  name: "Audiobook Narration",

  steps: () => [
    // Step 1 — Voice & script (narration__one, else-branch).
    {
      id: "details",
      title: "Voice & script",
      sub: "Describe the narration you want, your audition script helps us match the right narrator.",
      fields: [
        { type: "text", key: "title", label: "Title", required: true, placeholder: "Working title" },
        { type: "text", key: "subtitle", label: "Subtitle", required: false },
        {
          type: "toggle",
          key: "fiction",
          label: "This is fiction",
        },
        {
          type: "count",
          key: "character_voice_count",
          label: "Character voices",
          required: false,
          min: 1,
          max: 50,
          step: 1,
          unit: "voices",
          when: (values) => !!values.fiction,
        },
        {
          // Kept per ruling C11 (backend accepts trigger_warning + details); copy
          // restyled to the design's trigger-toggle idiom (fTriggerToggle).
          type: "triggerWarning",
          label: "May contain sensitive / triggering topics",
        },
        {
          type: "wordcount",
          key: "word_count",
          label: "Word count",
          required: true,
          min: 1000,
          step: 500,
        },
        {
          // Both niche lists are sorted to match the design (NICHES_SORTED) and the
          // live UI (both sort); membership is validated, order is not. The
          // fiction→genres source switch is backend-forced (step_validations.rb).
          type: "select",
          key: "niche",
          label: "Niche / genre",
          required: true,
          blankLabel: "Select Niche/Genre",
          options: (values) =>
            asOptions(
              values && values.fiction ? [...WRITING_GENRES].sort() : [...WRITING_NICHES].sort(),
            ),
        },
        {
          // The visible default carries the REAL enum value (open_to_all), not a
          // blank placeholder that submits nil and duplicates the first option
          // (Plumbline bug 9). `default` shows/keeps the enum's "no preference"
          // value; the server enum also defaults to open_to_all when untouched.
          type: "select",
          key: "accent",
          label: "Accent",
          required: false,
          default: "open_to_all",
          options: NARRATION_ACCENTS,
        },
        {
          type: "select",
          key: "gender",
          label: "Narrator gender",
          required: false,
          default: "male_or_female",
          options: NARRATION_GENDERS,
        },
        {
          type: "textarea",
          key: "audition_script",
          label: "Audition script",
          required: true,
          rows: 8,
          // The server has always required 30–300 words (StepValidations
          // .valid_word_count defaults min: 30); until this was declared the
          // floor first appeared as a 422 at the end of the flow.
          minWords: 30,
          maxWords: 300,
          help: "30 to 300 words — a snippet to audition narrators with.",
          placeholder: "Paste a short passage…",
        },
        {
          type: "file",
          key: "full_script",
          label: "Full script (manuscript)",
          required: true,
          accept: ".doc,.docx,.pdf,.zip",
          placeholder: "Upload your full script",
        },
        {
          type: "file",
          key: "requirements",
          label: "Requirements file",
          required: false,
          accept: ".doc,.docx,.pdf,.zip",
          placeholder: "Upload a file",
        },
      ],
    },
  ],

  // Instant estimate for the PriceRail. Ruby: (word_count / 100) * narrator_rate,
  // where `word_count / 100` is INTEGER division (floor), narrator_rate = $1.95.
  estimate: (values = {}, ctx = {}) => {
    const wc = Number(values.word_count) || 0
    const units = Math.floor(wc / 100)
    const amount = money(units * (ctx.rates?.narrator_cost ?? RATES.NARRATOR_COST))
    const lines = []

    if (wc > 0) {
      lines.push({ label: `Narration (${wc.toLocaleString()} words)`, amount })
    }

    return { lines, total: amount, isRange: false, framing: "free-to-post" }
  },

  // /post/price entry (camelCase; underscored server-side). cost_fields for
  // narration_posting = [word_count]. Null until a word count is set.
  pricePayload: (values = {}) => {
    const wc = Number(values.word_count) || 0
    if (wc <= 0) return null

    return {
      postingType: "narration_posting",
      wordCount: wc,
    }
  },
}
